import { css } from "glamor";
import { InfoTooltip } from "./InfoTooltip";
import {
    type FunnelData,
    type StepConfig,
    type StepComputed,
    FUNNEL_STEPS,
    TARGET_OVERALL,
    computeStepValues,
} from "./funnelCalc";

/* Theme-aware tokens: use CSS variables from .plh-dashboard so the tile respects light/dark theme */
const barBg = "var(--bar-bg)";
const surface1 = "var(--s1)";
const surfaceHover = "var(--s-hover)";
const border = "var(--brd)";
const textPrimary = "var(--t1)";
const textMuted = "var(--t3)";
const greenAccent = "var(--green-t)";
const redAccent = "var(--red-t)";
const greenDim = "var(--green-dim)";
const redDim = "var(--red-dim)";
const radius = 8;

/** Payload for opening the funnel step detail panel from a row click. */
export interface FunnelStepPanelPayload {
    stepKey: string;
    title: string;
    subtitle: string;
    actual: number | string;
    goal: number | null;
    vsPct: number | null;
    hit: boolean;
    prevWeekValue?: number | null;
    avg6WeekValue?: number | null;
    prevWeekLabel?: string;
    prevStepActual?: number | null;
    prevStepGoal?: number | null;
    percentModeLabel?: string;
}

interface ActivationFunnelTileProps {
    funnel: FunnelData;
    activatedToPaid: number;
    /** Optional history for the Activated -> Paid metric (percentages). */
    activatedToPaidHistory?: {
        current: number;
        prevWeek: number | null;
        avg6Weeks: number | null;
    };
    /** Paid customer counts for activated cohort. */
    paidFromActivatedCohort?: number;
    paidFromActivatedCohortHistory?: {
        current: number;
        prevWeek: number | null;
        avg6Weeks: number | null;
    };
    /** Selected ISO week number for dynamic Wk(n-1) labeling. */
    selectedWeek?: number;
    /** Overrides default previous-period label (e.g. previous calendar month). */
    prevPeriodLabel?: string;
    /** Appended to the main funnel info tooltip. */
    funnelTooltipExtra?: string;
    /** Drives "% of previous week/month" copy in step panels. */
    periodKind?: "week" | "month";
    /** User-edited goals per step key; overrides computed goals for bar scaling and target line. */
    goalOverrides?: Record<string, number>;
    onStepClick?: (payload: FunnelStepPanelPayload) => void;
}

// --- Subcomponents ---

function FunnelBar({
    width,
    targetLeft,
    gradient,
    hit = false,
    minWidth = 2,
}: {
    width: number;
    targetLeft: number | null;
    gradient: [string, string];
    hit?: boolean;
    minWidth?: number;
}) {
    const targetLineClr = hit ? greenAccent : redAccent;
    return (
        <div
            {...css({
                position: "relative",
                height: 28,
                background: barBg,
                borderRadius: 5,
                overflow: "visible",
            })}
        >
            <div
                {...css({
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: `${Math.max(width, width > 0 ? minWidth : 0)}%`,
                    background: `linear-gradient(90deg, ${gradient[0]}, ${gradient[1]})`,
                    borderRadius: 5,
                    zIndex: 1,
                    transition: "width .8s cubic-bezier(.22,1,.36,1)",
                    minWidth: width > 0 ? 6 : 2,
                })}
            />
            {targetLeft !== null && targetLeft > 0 && (
                <div
                    {...css({
                        position: "absolute",
                        top: -3,
                        bottom: -3,
                        left: `calc(${targetLeft}% - 1px)`,
                        width: 2,
                        background: targetLineClr,
                        zIndex: 2,
                        borderRadius: 1,
                    })}
                >
                    <span
                        {...css({
                            position: "absolute",
                            top: -11,
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: 7,
                            color: targetLineClr,
                            lineHeight: 1,
                        })}
                    >
                        ▾
                    </span>
                </div>
            )}
        </div>
    );
}

function FunnelDrop({ dropPct, dropNote, severity }: { dropPct: number; dropNote: string; severity: "sev" | "ok" }) {
    return (
        <div
            {...css({
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "1px 0 1px 24px",
                marginLeft: 156,
            })}
        >
            <div
                {...css({
                    width: 2,
                    height: 14,
                    background: barBg,
                    borderRadius: 1,
                })}
            />
            <span
                {...css({
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    padding: "1px 7px",
                    borderRadius: 10,
                    background: severity === "sev" ? redDim : greenDim,
                    color: severity === "sev" ? redAccent : greenAccent,
                })}
            >
                −{dropPct}%
            </span>
            <span
                {...css({
                    fontSize: "0.64rem",
                    color: textMuted,
                })}
            >
                {dropNote}
            </span>
        </div>
    );
}

function VisitsRow({
    step,
    computed,
    prevWeekLabel,
    onClick,
}: {
    step: StepConfig;
    computed: StepComputed;
    prevWeekLabel: string;
    onClick?: () => void;
}) {
    const subtitle = step.subtitle(
        computed.rate,
        undefined,
        typeof computed.count === "number" ? computed.count : undefined
    );
    const hit = computed.vsPct !== null && computed.vsPct >= 100;

    return (
        <div
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={
                onClick
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
            {...css({
                display: "grid",
                gridTemplateColumns: "180px 1fr 200px 120px",
                gap: 14,
                alignItems: "center",
                background: surfaceHover,
                border: `1px solid ${border}`,
                padding: "12px 18px",
                borderRadius: radius,
            })}
        >
            <div>
                <div
                    {...css({
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: textMuted,
                    })}
                >
                    {step.title}
                </div>
                <div
                    {...css({
                        fontSize: "0.65rem",
                        color: textMuted,
                        marginTop: 1,
                    })}
                >
                    {subtitle}
                </div>
            </div>
            <div />
            <div
                {...css({
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 4,
                })}
            >
                <div
                    {...css({
                        fontWeight: 700,
                        fontSize: "1.2rem",
                        lineHeight: 1,
                        display: "flex",
                        alignItems: "baseline",
                        gap: 8,
                        color: textPrimary,
                        fontFamily: "monospace",
                    })}
                >
                    {typeof computed.count === "number" ? computed.count.toLocaleString() : computed.count}
                </div>
                {(computed.prevWeekValue != null || computed.avg6WeekValue != null) && (
                    <div
                        {...css({
                            display: "flex",
                            gap: 4,
                            fontSize: "0.65rem",
                            flexWrap: "wrap",
                            justifyContent: "flex-end",
                        })}
                    >
                        {computed.prevWeekValue != null && (
                            <span
                                {...css({
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    border: `1px solid ${border}`,
                                    background: surface1,
                                    color: textMuted,
                                    fontWeight: 500,
                                    fontFamily: "monospace",
                                })}
                            >
                                {prevWeekLabel} <b>{computed.prevWeekValue}</b>
                            </span>
                        )}
                        {computed.avg6WeekValue != null && (
                            <span
                                {...css({
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    border: `1px solid ${border}`,
                                    background: surface1,
                                    color: textMuted,
                                    fontWeight: 500,
                                    fontFamily: "monospace",
                                })}
                            >
                                Avg <b>{computed.avg6WeekValue}</b>
                            </span>
                        )}
                    </div>
                )}
            </div>
            <div
                {...css({
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 3,
                })}
            >
                {computed.goalCount !== null && (
                    <span {...css({ fontSize: "0.68rem", color: textMuted, fontFamily: "monospace" })}>
                        Goal {computed.goalCount.toLocaleString()}
                    </span>
                )}
                {computed.vsPct !== null && (
                    <span
                        {...css({
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 4,
                            background: hit ? greenDim : redDim,
                            color: hit ? greenAccent : redAccent,
                        })}
                    >
                        {computed.vsPct}% {hit ? "✓" : "✗"}
                    </span>
                )}
            </div>
        </div>
    );
}

function FunnelRow({
    step,
    computed,
    isNorthStar,
    prevWeekLabel,
    onClick,
}: {
    step: StepConfig;
    computed: StepComputed;
    isNorthStar?: boolean;
    prevWeekLabel: string;
    onClick?: () => void;
}) {
    const subtitle = step.subtitle(computed.rate, undefined, typeof computed.count === "number" ? computed.count : undefined);
    const hit = computed.vsPct !== null && computed.vsPct >= 100;

    return (
        <div
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
            {...css({
                display: "grid",
                gridTemplateColumns: "180px 1fr 200px 120px",
                gap: 14,
                alignItems: "center",
                background: surface1,
                border: `1px solid ${isNorthStar ? greenDim : border}`,
                padding: "12px 18px",
                borderRadius: radius,
                transition: "background .15s",
                cursor: onClick ? "pointer" : "default",
                ":hover": { background: surfaceHover },
            })}
        >
            <div>
                <div
                    {...css({
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: isNorthStar ? greenAccent : textPrimary,
                    })}
                >
                    {step.title}
                </div>
                <div
                    {...css({
                        fontSize: "0.65rem",
                        color: textMuted,
                        marginTop: 1,
                    })}
                >
                    {subtitle}
                </div>
            </div>
            <FunnelBar
                width={computed.barWidth}
                targetLeft={computed.targetLeft}
                gradient={step.gradient}
                hit={computed.vsPct !== null && computed.vsPct >= 100}
                minWidth={computed.barWidth > 0 && computed.barWidth < 1 ? 4 : 6}
            />
            <div
                {...css({
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 4,
                })}
            >
                <div
                    {...css({
                        fontWeight: 700,
                        fontSize: "1.2rem",
                        lineHeight: 1,
                        display: "flex",
                        alignItems: "baseline",
                        gap: 8,
                        color: computed.count === 0 && step.isNorthStar ? redAccent : textPrimary,
                        fontFamily: "monospace",
                    })}
                >
                    {computed.count}
                    <span {...css({ fontSize: "0.7rem", fontWeight: 500, color: textMuted })}>
                        {computed.rate}%
                    </span>
                </div>
                {(computed.prevWeekValue != null || computed.avg6WeekValue != null) && (
                    <div
                        {...css({
                            display: "flex",
                            gap: 4,
                            fontSize: "0.65rem",
                            flexWrap: "wrap",
                            justifyContent: "flex-end",
                        })}
                    >
                        {computed.prevWeekValue != null && (
                            <span
                                {...css({
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    border: `1px solid ${border}`,
                                    background: surface1,
                                    color: textMuted,
                                    fontWeight: 500,
                                    fontFamily: "monospace",
                                })}
                            >
                                {prevWeekLabel} <b>{computed.prevWeekValue}</b>
                            </span>
                        )}
                        {computed.avg6WeekValue != null && (
                            <span
                                {...css({
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    border: `1px solid ${border}`,
                                    background: surface1,
                                    color: textMuted,
                                    fontWeight: 500,
                                    fontFamily: "monospace",
                                })}
                            >
                                Avg <b>{computed.avg6WeekValue}</b>
                            </span>
                        )}
                    </div>
                )}
            </div>
            <div
                {...css({
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 3,
                })}
            >
                {computed.goalCount !== null && (
                    <span {...css({ fontSize: "0.68rem", color: textMuted, fontFamily: "monospace" })}>
                        Goal {computed.goalCount}
                    </span>
                )}
                {computed.vsPct !== null && (
                    <span
                        {...css({
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 4,
                            background: hit ? greenDim : redDim,
                            color: hit ? greenAccent : redAccent,
                        })}
                    >
                        {computed.vsPct}% {hit ? "✓" : "✗"}
                    </span>
                )}
            </div>
        </div>
    );
}

export const ActivationFunnelTile = ({
    funnel,
    activatedToPaid,
    paidFromActivatedCohortHistory,
    selectedWeek,
    prevPeriodLabel,
    funnelTooltipExtra,
    periodKind = "week",
    goalOverrides = {},
    onStepClick,
}: ActivationFunnelTileProps) => {
    const total = funnel.webVisits || 1;

    const prevWeekLabel =
        prevPeriodLabel ??
        (selectedWeek && selectedWeek > 1 ? `Wk${selectedWeek - 1}` : "Prev wk");

    const percentVsPrevLabel =
        periodKind === "month" ? "% of previous month" : "% of previous week";

    // Map from dataKey to step key so each step can look up its parent step's effective goal via prevKey
    const dataKeyToStepKey: Record<string, string> = {};
    for (const s of FUNNEL_STEPS) dataKeyToStepKey[s.dataKey as string] = s.key;

    const paidAbsolute = paidFromActivatedCohortHistory?.current ?? 0;

    // First pass: compute all steps sequentially so each step's goal cascades from the parent step's effective goal
    const goalByStepKey: Record<string, number | null> = {};
    const stepsWithComputedBase: Array<{ step: StepConfig; computed: StepComputed }> = [];
    for (const step of FUNNEL_STEPS) {
        const parentStepKey = step.prevKey ? dataKeyToStepKey[step.prevKey] : undefined;
        const parentGoal = parentStepKey != null ? (goalByStepKey[parentStepKey] ?? null) : null;
        const computed = computeStepValues(step, funnel, activatedToPaid, total, 1, goalOverrides, undefined, parentGoal, paidAbsolute);
        stepsWithComputedBase.push({ step, computed });
        goalByStepKey[step.key] = computed.goalCount;
    }

    const signupsStepBase = stepsWithComputedBase.find(({ step }) => step.key === "signups");
    const signupActual = funnel.icpSignups;
    const signupGoal = signupsStepBase?.computed.goalCount ?? null;
    const signupScale =
        signupGoal != null
            ? signupActual >= signupGoal
                ? signupActual
                : signupGoal
            : signupActual;
    const scaleForBars = signupScale > 0 ? signupScale : 1;

    const goalByStepKey2: Record<string, number | null> = {};
    const stepsWithComputed: Array<{ step: StepConfig; computed: StepComputed }> = [];
    for (const step of FUNNEL_STEPS) {
        const parentStepKey = step.prevKey ? dataKeyToStepKey[step.prevKey] : undefined;
        const parentGoal = parentStepKey != null ? (goalByStepKey2[parentStepKey] ?? null) : null;
        const computed = computeStepValues(
            step,
            funnel,
            activatedToPaid,
            total,
            1,
            goalOverrides,
            scaleForBars,
            parentGoal,
            paidAbsolute
        );

        if (step.key === "paid" && paidFromActivatedCohortHistory) {
            computed.prevWeekValue = paidFromActivatedCohortHistory.prevWeek ?? null;
            computed.avg6WeekValue = paidFromActivatedCohortHistory.avg6Weeks ?? null;
        }

        stepsWithComputed.push({ step, computed });
        goalByStepKey2[step.key] = computed.goalCount;
    }

    const visitsStep = stepsWithComputed.find(({ step }) => step.key === "webVisits");
    const barSteps = stepsWithComputed.filter(
        ({ step }) => !["webVisits", "scheduleGenerated", "schedulePublished"].includes(step.key)
    );

    const stepActualByDataKey: Record<string, number> = {
        webVisits: funnel.webVisits,
        icpSignups: funnel.icpSignups,
        signupReasonNotNull: funnel.signupReasonNotNull,
        onboardingStarted: funnel.onboardingStarted,
        scheduleGenerated: funnel.scheduleGenerated,
        schedulePublished: funnel.schedulePublished,
        activated: funnel.activated,
    };

    const getPrevStepActual = (step: StepConfig): number | null => {
        if (!step.prevKey) return null;
        return stepActualByDataKey[step.prevKey] ?? null;
    };

    const overallConversionPct = (funnel.schedulePublished / total) * 100;

    return (
        <div
            {...css({
                background: surface1,
                borderRadius: 12,
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)",
                border: `1px solid ${border}`,
                padding: 24,
            })}
        >
            <div
                {...css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 8,
                    marginBottom: 12,
                    flexWrap: "wrap",
                })}
            >
                <div {...css({ display: "flex", alignItems: "center", gap: 8 })}>
                    <div {...css({ width: 8, height: 8, borderRadius: "50%", background: greenAccent })} />
                    <span {...css({ fontSize: 12, fontWeight: 500, color: greenAccent })}>PostHog API</span>
                </div>
                <InfoTooltip
                    text={`Step‑by‑step conversion from unique web visits to paid accounts. Includes targets for key conversion steps.${funnelTooltipExtra ? ` ${funnelTooltipExtra}` : ""}`}
                />
            </div>

            <div
                {...css({
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    marginBottom: 12,
                })}
            >
                <h2 {...css({ fontSize: "1rem", fontWeight: 700, letterSpacing: -0.3, margin: 0, color: textPrimary })}>
                    Activation Funnel
                </h2>
                <div {...css({ fontSize: "0.72rem", color: textMuted })}>
                    End-to-end: <b {...css({ color: overallConversionPct >= TARGET_OVERALL ? greenAccent : redAccent, fontWeight: 700, fontSize: "0.82rem" })}>{overallConversionPct.toFixed(2)}%</b> · target {TARGET_OVERALL}%
                </div>
            </div>

            <div {...css({ display: "flex", flexDirection: "column", gap: 2 })}>
                {visitsStep && (
                    <>
                        <VisitsRow
                            step={visitsStep.step}
                            computed={visitsStep.computed}
                            prevWeekLabel={prevWeekLabel}
                            onClick={
                                onStepClick
                                    ? () =>
                                          onStepClick({
                                              stepKey: visitsStep.step.key,
                                              title: visitsStep.step.title,
                                              subtitle: visitsStep.step.subtitle(
                                                  visitsStep.computed.rate,
                                                  undefined,
                                                  typeof visitsStep.computed.count === "number"
                                                      ? visitsStep.computed.count
                                                      : undefined
                                              ),
                                              actual: visitsStep.computed.count,
                                              goal: visitsStep.computed.goalCount,
                                              vsPct: visitsStep.computed.vsPct,
                                              hit:
                                                  visitsStep.computed.vsPct !== null &&
                                                  visitsStep.computed.vsPct >= 100,
                                              prevWeekValue: visitsStep.computed.prevWeekValue,
                                              avg6WeekValue: visitsStep.computed.avg6WeekValue,
                                              prevWeekLabel,
                                              prevStepActual: visitsStep.computed.prevWeekValue ?? null,
                                              percentModeLabel: percentVsPrevLabel,
                                          })
                                    : undefined
                            }
                        />
                        {barSteps.length > 0 && (
                            <FunnelDrop
                                dropPct={barSteps[0].computed.dropPct}
                                dropNote={barSteps[0].computed.dropNote}
                                severity={barSteps[0].computed.dropSeverity}
                            />
                        )}
                    </>
                )}

                {barSteps.map(({ step, computed }, i) => {
                    const subtitle = step.subtitle(
                        computed.rate,
                        undefined,
                        typeof computed.count === "number" ? computed.count : undefined
                    );
                    const hit = computed.vsPct !== null && computed.vsPct >= 100;
                    return (
                        <div key={step.key}>
                            <FunnelRow
                                step={step}
                                computed={computed}
                                isNorthStar={step.isNorthStar}
                                prevWeekLabel={prevWeekLabel}
                                onClick={
                                    onStepClick
                                        ? () =>
                                              onStepClick({
                                                  stepKey: step.key,
                                                  title: step.title,
                                                  subtitle,
                                                  actual: computed.count,
                                                  goal: computed.goalCount,
                                                  vsPct: computed.vsPct,
                                                  hit,
                                                  prevWeekValue: computed.prevWeekValue,
                                                  avg6WeekValue: computed.avg6WeekValue,
                                                  prevWeekLabel,
                                                  prevStepActual: getPrevStepActual(step),
                                                  prevStepGoal: step.prevKey
                                                      ? (goalByStepKey2[dataKeyToStepKey[step.prevKey]] ?? null)
                                                      : null,
                                              })
                                        : undefined
                                }
                            />
                            {i < barSteps.length - 1 && (
                                <FunnelDrop
                                    dropPct={barSteps[i + 1].computed.dropPct}
                                    dropNote={barSteps[i + 1].computed.dropNote}
                                    severity={barSteps[i + 1].computed.dropSeverity}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            <div
                {...css({
                    marginTop: 24,
                    paddingTop: 24,
                    borderTop: `1px solid ${border}`,
                })}
            >
                <div
                    {...css({
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    })}
                >
                    <span {...css({ fontSize: 16, fontWeight: 500, color: textPrimary })}>Overall Conversion</span>
                    <div {...css({ display: "flex", flexDirection: "column", alignItems: "flex-end" })}>
                        <span
                            {...css({
                                fontSize: 20,
                                fontWeight: 600,
                                color: overallConversionPct >= TARGET_OVERALL ? greenAccent : redAccent,
                            })}
                        >
                            {overallConversionPct.toFixed(2)}%
                        </span>
                        <span
                            {...css({
                                fontSize: 13,
                                color: overallConversionPct >= TARGET_OVERALL ? greenAccent : redAccent,
                            })}
                        >
                            Target: {TARGET_OVERALL}%
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};
